import {ClassNames} from '../Interfaces/class-names';
import {DomainResponse, FetchDomain} from '../Helpers/Fetch';
import {wrap} from '../Helpers/wrap';
import {Locale} from '../Interfaces/locale';
import Cart from './Cart';
import {decodeHtmlText} from '../Helpers/sanitize';

export default class SearchAlternativeRow {
    public readonly element: HTMLRsHeaderElement;
    private readonly classNames: ClassNames;
    private readonly header: HTMLRsHeaderElement;
    private readonly prefix: string;
    private readonly suffix: string;
    private readonly getItNow: HTMLSpanElement;
    private readonly domain: string;
    private locale: Locale;
    private cart: Cart;
    private pricing: HTMLDivElement;
    private blurb: HTMLSpanElement;
    private domainDetails: DomainResponse;

    public constructor({
                           element,
                           prefix,
                           suffix,
                           classNames,
                           locale,
                           cart,
                           knownDetails,
                           signal,
                       }: {
        element: HTMLRsHeaderElement;
        prefix: string;
        suffix: string;
        classNames: ClassNames;
        locale: Locale;
        cart: Cart;
        /** Details already returned by an earlier lookup; skips the per-row fetch. */
        knownDetails?: DomainResponse;
        /** Aborts the per-row fetch when the results list is replaced. */
        signal?: AbortSignal;
    }) {
        this.element = element;
        this.prefix = prefix;
        this.suffix = suffix;
        this.locale = locale;
        this.cart = cart;
        this.domain = this.prefix + this.suffix;
        this.classNames = classNames;
        this.header = this.element.querySelector(
            `.${this.classNames.searchAlternativeRowHeader}`
        );
        this.getItNow = this.element.querySelector(
            `.${this.classNames.getItNowButton}`
        );
        this.pricing = this.element.querySelector(
            `.${this.classNames.searchAlternativePricingContainer}`
        );

        this.build();
        if (knownDetails) {
            this.render(knownDetails);
        } else {
            this.fetchDetails(signal);
        }
    }

    private build(): void {
        const suffix = Object.assign(document.createElement('span'), {
            className: this.classNames.searchAlternativeDomainSuffix,
            textContent: this.suffix,
        });
        this.header.textContent = this.prefix;
        this.header.appendChild(suffix);
    }

    private addListeners(): void {
        this.element.addEventListener('click', () => {
            if (!this.getItNow) {
                console.error('getItNow element not found');
                return;
            }

            if (this.getItNow.classList.contains(this.classNames.getItNowButtonSelected)) {
                // Revert to original state and remove domain from array
                this.getItNow.classList.remove(this.classNames.getItNowButtonSelected);
                this.getItNow.textContent = 'Get it now';
                // Remove domain from the shopping cart
                this.cart.removeDomain(this.domain);
            } else {
                // Change to selected state with tick icon and add domain to array
                this.getItNow.classList.add(this.classNames.getItNowButtonSelected);
                this.showTick();
                this.cart.addDomain(this.domain, this.domainDetails);
            }
        });
        // Check if the domain is already in the cart
        if (this.cart.isInCart(this.domain)) {
            this.getItNow.classList.add(this.classNames.getItNowButtonSelected);
            this.showTick();
        }
    }

    private showTick(): void {
        const tick = document.createElement('rs-icon');
        tick.setAttribute('name', 'tick');
        tick.setAttribute('color', 'white');
        this.getItNow.textContent = '';
        this.getItNow.appendChild(tick);
    }

    private fetchDetails(signal?: AbortSignal): void {
        FetchDomain(this.domain, this.locale, 0, signal)
            .then((domainResponse) => this.render(domainResponse))
            .catch(() => {
                this.element.remove();
            });
    }

    private render(domainResponse: DomainResponse): void {
        const {isAvailable, success, pricing} = domainResponse;

        if (!success) {
            this.domainDetails = null;
            this.element.remove();
            return;
        }
        this.domainDetails = domainResponse;

        this.element.classList.remove(this.classNames.searchAlternativeRowFetching);
        this.getItNow.textContent = 'Get it now';
        if (!isAvailable) {
            this.element.classList.add(this.classNames.searchAlternativeRowUnavailable);
            this.getItNow.classList.add(this.classNames.getItNowButtonUnavailable);
            this.getItNow.textContent = 'UNAVAILABLE';
            this.getItNow.setAttribute('disabled', 'true');
            const mainContainer = document.querySelector(`.${this.classNames.domainSearchContainer}`);
            if (mainContainer) {
                mainContainer.appendChild(this.element);
            }
            return;
        }

        if (!Array.isArray(pricing) || pricing.length === 0) {
            this.element.remove();
            return;
        }

        this.pricing.textContent = '';
        const priceWrapper = document.createElement('div');
        if (pricing[0].originalPrice !== pricing[0].price) {
            const discount = Object.assign(document.createElement('span'), {
                className: this.classNames.searchAlternativePricingDiscount,
                textContent: decodeHtmlText(pricing[0].originalPrice),
            });
            const price = Object.assign(document.createElement('span'), {
                className: this.classNames.searchAlternativePricing,
                textContent: decodeHtmlText(pricing[0].price),
            });
            priceWrapper.appendChild(discount);
            priceWrapper.appendChild(document.createTextNode(' '));
            priceWrapper.appendChild(price);
        } else {
            priceWrapper.className = this.classNames.searchAlternativePricing;
            priceWrapper.textContent = decodeHtmlText(pricing[0].price);
        }
        this.pricing.appendChild(priceWrapper);

        this.blurb = Object.assign(document.createElement('div'), {
            textContent: 'For the first year',
            className: this.classNames.searchAlternativePricingBlurb,
        });
        wrap(this.blurb, this.pricing);

        this.addListeners();
    }
}
